/* eslint-disable max-lines */
import { observable, action, computed, set } from 'mobx';

import { getActiveLanguageCode, translate } from 'services/i18nService';
import messagingService from 'services/MessagingService/MessagingService';
import {
  GET_PRIVATE_DIRECTORY_CAMS_ERROR,
  GET_PRIVATE_DIRECTORY_CAMS_RESPONSE,
  GET_PRIVATE_DIRECTORY_CAROUSEL_CAMS_ERROR,
  GET_PRIVATE_DIRECTORY_CAROUSEL_CAMS_RESPONSE,
  GET_PRIVATE_DIRECTORY_FILTERS_RESPONSE,
  GET_PRIVATE_DIRECTORY_KINKS_RESPONSE,
  GET_PRIVATE_DIRECTORY_USER_PRIVATE_SHOW_SETTINGS,
  PRIVATE_DIRECTORY_PRIVATE_SHOW_CANCEL,
  PRIVATE_DIRECTORY_PRIVATE_SHOW_SENT,
  PRIVATE_DIRECTORY_USER_BALANCE,
  PRIVATE_DIRECTORY_USER_NOTIFICATION,
  PRIVATE_DIRECTORY_USER_NOTIFICATION_CHANGED,
  PRIVATE_DIRECTORY_ROOM_OFFLINE,
  SHOW_BUY_TOKENS_MODAL_TRIGGER,
} from 'services/MessagingService/EventNames';
import MetaService from 'services/MetaService';
import NotificationService from 'services/NotificationService';
import ProfileService from 'services/ProfileService';
import APIStates from 'enum/APIStates';
import { capitalize } from 'utils/helpers';
import parseCatch from 'utils/parseCatch';

import PrivateDirectoryService from './PrivateDirectoryService';
import {
  CAMS_PER_PAGE,
  DEFAULT_ACTIVE_PAGE,
  DEFAULT_FILTERS_VALUE,
  GENDER,
  SORT_OPTION,
  VIEW,
  getTokenOptions,
} from './config';


class PrivateDirectoryStore {
  constructor() {
    this.metaService = new MetaService();
    this.messagingService = messagingService();
    this.messagingService.on(GET_PRIVATE_DIRECTORY_CAMS_RESPONSE, this._handleCamsResponse);
    this.messagingService.on(GET_PRIVATE_DIRECTORY_CAMS_ERROR, this._handleCamsError);
    this.messagingService.on(GET_PRIVATE_DIRECTORY_CAROUSEL_CAMS_RESPONSE, this._handleSliderCamsResponse);
    this.messagingService.on(GET_PRIVATE_DIRECTORY_CAROUSEL_CAMS_ERROR, this._handleSliderCamsError);
    this.messagingService.on(GET_PRIVATE_DIRECTORY_FILTERS_RESPONSE, this._handleFiltersResponse);
    this.messagingService.on(GET_PRIVATE_DIRECTORY_KINKS_RESPONSE, this._handleKinksResponse);
    this.messagingService.on(GET_PRIVATE_DIRECTORY_USER_PRIVATE_SHOW_SETTINGS, this._handleUserSettingsResponse);
    this.messagingService.on(PRIVATE_DIRECTORY_PRIVATE_SHOW_SENT, this._handlePostCall);
    this.messagingService.on(PRIVATE_DIRECTORY_USER_NOTIFICATION, this.userNotification);
    this.messagingService.on(PRIVATE_DIRECTORY_PRIVATE_SHOW_CANCEL, this._handleCancelCall);
    this.messagingService.on(PRIVATE_DIRECTORY_USER_BALANCE, this.handleUserBalance);
    this.messagingService.on(PRIVATE_DIRECTORY_USER_NOTIFICATION_CHANGED, this.handleUserNotificationChanged);
    this.messagingService.on(PRIVATE_DIRECTORY_ROOM_OFFLINE, this.handleRoomOffline);
  }

  destructor() {
    this.messagingService.off(GET_PRIVATE_DIRECTORY_CAMS_RESPONSE);
    this.messagingService.off(GET_PRIVATE_DIRECTORY_CAMS_ERROR);
    this.messagingService.off(GET_PRIVATE_DIRECTORY_CAROUSEL_CAMS_RESPONSE);
    this.messagingService.off(GET_PRIVATE_DIRECTORY_CAROUSEL_CAMS_ERROR);
    this.messagingService.off(GET_PRIVATE_DIRECTORY_FILTERS_RESPONSE);
    this.messagingService.off(GET_PRIVATE_DIRECTORY_KINKS_RESPONSE);
    this.messagingService.off(GET_PRIVATE_DIRECTORY_USER_PRIVATE_SHOW_SETTINGS);
    this.messagingService.off(PRIVATE_DIRECTORY_PRIVATE_SHOW_SENT);
    this.messagingService.off(PRIVATE_DIRECTORY_PRIVATE_SHOW_CANCEL);
    this.messagingService.off(PRIVATE_DIRECTORY_USER_BALANCE);
    this.messagingService.off(PRIVATE_DIRECTORY_USER_NOTIFICATION_CHANGED);
    this.messagingService.off(PRIVATE_DIRECTORY_ROOM_OFFLINE);
    this.messagingService.off(PRIVATE_DIRECTORY_USER_NOTIFICATION);
  }

  initValues = {
    username: undefined,
    realCountry: undefined,
    genderPreference: undefined,
    filter: undefined,
    kink: undefined,
  };

  @observable cams = {
    data: [],
    totalCount: 0,
    status: APIStates.INITIAL,
    error: null,
  };

  @observable sliderCams = {
    data: [],
    status: APIStates.INITIAL,
    error: null,
  };

  @observable genderPreference = GENDER.FEMALE;
  @observable activePage = DEFAULT_ACTIVE_PAGE;
  @observable sortOptionValue = SORT_OPTION.VIDEO_QUALITY;
  @observable filterRadioValue = DEFAULT_FILTERS_VALUE;
  @observable tokensRadioValue = DEFAULT_FILTERS_VALUE;
  @observable kinkRadioValue = DEFAULT_FILTERS_VALUE;
  @observable profileInfo = null;
  @observable filtersData = null;
  @observable kinksData = null;
  @observable activeMobileView = VIEW.LIST;
  @observable showLoadingOverlay = {
    show: false,
    index: null,
  };

  @observable userPrivateShowSettings = null;
  @observable priceForBlockOfTimeAndPerMinute = null;
  @observable postResponse = null;
  @observable performerName = null;
  @observable notificationUsername = '';
  @observable notificationImage = '';
  @observable userNotificationObj = null;
  @observable time = null;
  @observable userBalance = null;
  @observable userNotificationChanged = false;
  @observable alreadyHasRequest = false;
  @observable selectedBlockOfTimeOption = null;
  @observable roomOffline = false;
  @observable roomName = null;
  @observable username = null;
  @observable accessHash = null;

  clickedKink = null;
  roomName = null;
  username = null;
  accessHash = null;
  rest = null;

  @action.bound getSliderCams() {
    set(this.sliderCams, { status: APIStates.PENDING });
    PrivateDirectoryService.getSliderCams(this.genderPreference);
  }

  /**
   * @method getNotificationData - retrieving the username and image from the broadcaster
   * @param {string} username - broadcaster username
   * @param {string} imageSource - broadcaster imageSource
   */
  @action getNotificationData = (username, imageSource) => {
    this.notificationUsername = username;
    this.notificationImage = imageSource;
  };

  /**
   * @method setShowLoadingOverlay - setting the loading state
   * @param {bool} show
   */
  @action setShowLoadingOverlay = (show) => {
    set(this.showLoadingOverlay, { show });
  };

  @action setShowLoadingOverlayIndex = (index) => {
    set(this.showLoadingOverlay, { index });
  };

  /**
   * @method _handleSliderCamsResponse - internal function which transforms the response and
   * sets the carausel / slider data
   * @param {object} response
   */
  @action _handleSliderCamsResponse = (response) => {
    const { privateStreamers } = response;
    const data = privateStreamers.length > 0 ? this._transformCamsData(privateStreamers) : [];
    set(this.sliderCams, {
      data,
      status: APIStates.LOADED,
    });

    this.metaService.setMetaImage(privateStreamers[0].image);
  };

  /**
   * @method getPrivateShowAction - calling the API getPrivateShowAction from PrivateDirectoryService
   * @param {string} action - startPS/ request / cancelRequest
   * @param {string} roomname - broadcaster username
   * @param {string} username - username of the user that requests the PS
   * @param {string} accessHash - accessHash of the user that requests the PS
   * @param {Object} rest
   */
  @action getPrivateShowAction({ action, roomname, username, accessHash, ...rest }) {
    this.postResponse = null;
    this.userNotificationObj = null;
    this.time = null;
    this.roomName = roomname;
    this.username = username;
    this.accessHash = accessHash;
    PrivateDirectoryService.getPrivateShowAction({
      action,
      roomname,
      username,
      accessHash,
      ...rest,
    });
  }

  /**
   * @method privateShowAction - calling the API privateShowAction from PrivateDirectoryService
   * @param {string} action - startPS/ request / cancelRequest
   * @param {string} roomname - broadcaster username
   * @param {string} username - username of the user that requests the PS
   * @param {string} accessHash - accessHash of the user that requests the PS
   * @param {Object} rest
   */
  @action privateShowAction({ action, roomname, username, accessHash, ...rest }) {
    this.postResponse = null;
    this.userNotificationObj = null;
    this.time = null;
    PrivateDirectoryService.privateShowAction(
      {
        action,
        roomname,
        username,
        accessHash,
        ...rest,
      },
      true
    );
  }

  /**
   * @method setelectedBlockOfTime - setting the selected value from block of time object
   * @param {*} data - index of selected value
   */
  @action setelectedBlockOfTime = (data) => {
    this.selectedBlockOfTimeOption = data;
  };

  /**
   * @method isLoadingStateSlider - if API state is pending show the mock (skelet) in the header
   */
  @computed get isLoadingStateSlider() {
    return this.sliderCams.status === APIStates.PENDING;
  }

  /**
   * @method isLoadedStateSlider - if API state is loaded replace the mock (skelet) with the real data
   */
  @computed get isLoadedStateSlider() {
    return this.sliderCams.status === APIStates.LOADED && this.sliderCams.data.length > 0;
  }

  /**
   * @method isEmptyStateSlider - if API state is loaded and there is no data show no results found
   */
  @computed get isEmptyStateSlider() {
    return this.sliderCams.status === APIStates.LOADED && this.sliderCams.data.length === 0;
  }

  /**
   * @method _handleSliderCamsError - handling API errors for slider cams
   * @param {Object} error
   */
  _handleSliderCamsError = (error) => {
    set(this.sliderCams, {
      status: APIStates.ERROR,
      error,
      data: [],
    });
  };

  /**
   * Cams
   */

  /**
   * @method getCams - retriving cams
   */
  @action.bound async getCams() {
    set(this.cams, { status: APIStates.PENDING });

    const filterQuery = this._getActiveFilterQuery();
    const tokensQuery = this._getActiveTokensQuery();
    const kinkQuery = this._getActiveKinkQuery();

    PrivateDirectoryService.getCams({
      page: this.activePage,
      gender: this.genderPreference,
      orderBy: this.sortOptionValue,
      ...(filterQuery && { ...filterQuery }),
      ...(tokensQuery && { ...tokensQuery }),
      ...(kinkQuery && { ...kinkQuery }),
    });
  }

  /**
   * @method setMetaContent - setting the meta content for PPD
   */
  @action setMetaContent = () => {
    this.metaService.setMetaContent(this.metaDescription, this.metaTitle);
  };

  /**
   * @method setMetaUrl - setting the meta url for PPD
   */
  @action setMetaUrl = () => {
    this.metaService.setMetaURL();
  };

  /**
   * @method _handleCamsResponse - handling the cams response
   * @param {Object} response
   */
  @action _handleCamsResponse = (response) => {
    const { privateStreamers, totalCount } = response;

    const data = privateStreamers.length > 0 ? this._transformCamsData(privateStreamers) : [];

    if (privateStreamers.length === 0) {
      this.activeMobileView = VIEW.LIST;
    }

    set(this.cams, {
      data,
      totalCount,
      status: APIStates.LOADED,
    });
  };

  /**
   * @method handleRoomOffline - sets the value if the room went offline (from firebase, userNotification node)
   */
  @action handleRoomOffline = () => {
    this.roomOffline = true;
  };

  /**
   * @method handleImage404Error - handles if the image for slider cant be found
   * @param {number} key - index of the cam in slider
   */
  @action handleImage404Error = (key) => {
    const sliderCams = this.sliderCams.data.map((item, idx) => {
      return {
        ...item,
        image: key === idx ? item.defaultImage : item.image,
      };
    });
    this.sliderCams.data = sliderCams;
  };

  /**
   * @method onPageChange - handling page change
   * @param {number} page
   */
  @action.bound onPageChange = (page) => {
    this.activePage = page.selected + 1;
  };

  /**
   * @method onNextPage - setting the active page + 1
   */
  @action.bound onNextPage = () => {
    this.activePage += 1;
  };

  /**
   * @method onPrevPage - setting the active page - 1
   */
  @action.bound onPrevPage = () => {
    this.activePage -= 1;
  };

  /**
   * @method isLoadingState - setting pending state for showing mock data for thumbnails, kinks, filters
   */
  @computed get isLoadingState() {
    return this.cams.status === APIStates.PENDING;
  }

  /**
   * @method isLoadedState - removing pending state for showing mock data for thumbnails, kinks, filters
   */
  @computed get isLoadedState() {
    return this.cams.status === APIStates.LOADED && this.cams.data.length > 0;
  }

  /**
   * @method isEmptyState - if not data is returned, show no results found (<EmptyState/> component)
   */
  @computed get isEmptyState() {
    return this.cams.status === APIStates.LOADED && this.cams.data.length === 0;
  }

  /**
   * @method isErrorState - if some errors are returned, show something went wrong (<ErrorState/> component)
   */
  @computed get isErrorState() {
    return this.cams.status === APIStates.ERROR;
  }

  /**
   * @method _transformCamsData - transforms the data from the API
   * @param {Object} data
   * @returns {Object} transformed data
   */

  _transformCamsData = (data) =>
    data.map(({ age, username, gender, image, countryCode, online, price, description, kinks, broadcastType }) => {
      return {
        age,
        username,
        gender,
        image,
        country: countryCode,
        online,
        price: price.toFixed(1).replace(/\.0$/, ''),
        description,
        tags: kinks,
        profileLink: `/${username}`,
        broadcastType,
      };
    });

  /**
   * @method _handleCamsError - sets the error for the cams
   * @param {*} error
   */
  _handleCamsError = (error) => {
    set(this.cams, {
      status: APIStates.ERROR,
      totalCount: 0,
      error,
    });
  };

  /**
   * Filters and kinks
   */

  /**
   * @method handleFilterChange - handles the change of the filter value and calls the corresponding API
   * @param {String} value
   */
  @action handleFilterChange = (value) => {
    this.activePage = DEFAULT_ACTIVE_PAGE;
    this.filterRadioValue = value;
    this.tokensRadioValue = DEFAULT_FILTERS_VALUE;
    this.kinkRadioValue = DEFAULT_FILTERS_VALUE;
    this.clickedKink = null;

    const filterQuery = this._getActiveFilterQuery();

    PrivateDirectoryService.getKinks({
      genderPreference: this.genderPreference,
      ...(filterQuery && { ...filterQuery }),
    });
  };

  /**
   * @method handleTokensChange - handles the price (token) change filter and calls the corresponding API
   * @param {String} value
   */
  @action handleTokensChange = (value) => {
    this.activePage = DEFAULT_ACTIVE_PAGE;
    this.tokensRadioValue = value;

    const filterQuery = this._getActiveFilterQuery();
    const tokensQuery = this._getActiveTokensQuery();
    const kinkQuery = this._getActiveKinkQuery();

    PrivateDirectoryService.getKinks({
      genderPreference: this.genderPreference,
      ...kinkQuery,
      ...(filterQuery && { ...filterQuery }),
      ...(tokensQuery && { ...tokensQuery }),
    });
  };

  /**
   * @method handleKinkChange - handles the kinks change filter and calls the corresponding API
   * @param {String} value
   */
  @action handleKinkChange = (value) => {
    this.activePage = DEFAULT_ACTIVE_PAGE;
    this.kinkRadioValue = value;
    this.clickedKink = this._getActiveKink();

    const filterQuery = this._getActiveFilterQuery();
    const tokensQuery = this._getActiveTokensQuery();
    const kinkQuery = this._getActiveKinkQuery();

    if (value !== DEFAULT_FILTERS_VALUE) {
      PrivateDirectoryService.getKinks({
        genderPreference: this.genderPreference,
        ...kinkQuery,
        ...(filterQuery && { ...filterQuery }),
        ...(tokensQuery && { ...tokensQuery }),
      });
    } else {
      PrivateDirectoryService.getKinks({
        genderPreference: this.genderPreference,
        ...(filterQuery && { ...filterQuery }),
        ...(tokensQuery && { ...tokensQuery }),
      });
    }
  };

  /**
   * @method getPerformerName - gets the name of the broadcaster
   * @param {String} performerName - broadcaster name
   */
  @action getPerformerName = (performerName) => {
    this.performerName = performerName;
  };

  /**
   * @method setTime - sets the remaining time for the call to expire
   * @param {number} remainingTime
   */
  @action setTime = (remainingTime) => {
    this.time = remainingTime;
  };

  /**
   * @method getParams - if the filters change from default values
   * @returns {Object} params
   */
  @computed get getParams() {
    const [filter, kink] = [this.filterRadioValue, this.kinkRadioValue];

    let params = undefined;

    if (filter !== DEFAULT_FILTERS_VALUE && kink !== DEFAULT_FILTERS_VALUE) {
      params = {
        filter,
        kink,
      };
    }

    if (filter !== DEFAULT_FILTERS_VALUE && kink === DEFAULT_FILTERS_VALUE) {
      params = { filter };
    }

    if (filter === DEFAULT_FILTERS_VALUE && kink !== DEFAULT_FILTERS_VALUE) {
      params = {
        filter,
        kink,
      };
    }

    return params;
  }

  /**
   * @method filters - gets the filters depending on the user info (language, country)
   * @returns {Object} filters
   */
  @computed get filters() {
    if (this.filtersData) {
      const userCountry = this.profileInfo?.countryId || this.initValues.realCountry;
      const filterCountry = this.filtersData.countries.find((country) => country.code === userCountry);

      const userLanguage = this.profileInfo?.mainLanguage || getActiveLanguageCode();
      const filterLanguage = this.filtersData.languages.find((language) => language.code === userLanguage);

      const filters = [
        {
          label: translate('directory.directory.private.filters.all.shows'),
          metaLabel: translate('directory.directory.private.all.text'),
          labelUrl: this._calculateLabelUrl(DEFAULT_FILTERS_VALUE),
          value: DEFAULT_FILTERS_VALUE,
          query: null,
        },
        {
          label: translate('directory.camtocam.shows'),
          metaLabel: 'C2C',
          labelUrl: this._calculateLabelUrl('c2c'),
          value: 'c2c',
          query: { 'c2cAllowed': true },
        },
        {
          label: translate(
            'directory.directory.private.filters.country.shows',
            { 0: translate(`countries.${filterCountry.code}`) }
          ),
          metaLabel: translate(`countries.${filterCountry.code}`),
          labelUrl: this._calculateLabelUrl(filterCountry.name),
          value: this._slugifyUrl(filterCountry.name),
          query: { country: filterCountry.code },
        },
        {
          label: translate(
            'directory.directory.private.filters.language.shows',
            { 0: translate(`languages.${filterLanguage.code}`) }
          ),
          metaLabel: translate(`languages.${filterLanguage.code}`),
          labelUrl: this._calculateLabelUrl(filterLanguage.name),
          value: this._slugifyUrl(filterLanguage.name),
          query: { language: filterLanguage.code },
        },
      ];

      const friendsAndFavorites = {
        label: translate('directory.directory.private.filters.fandf.shows'),
        metaLabel: translate('friendsfavorites.friendsfavorites.title'),
        labelUrl: this._calculateLabelUrl('friends-and-favorites'),
        value: 'friends-and-favorites',
        query: {
          friends: true,
          favorites: true,
        },
      };

      if (this.initValues.username) {
        filters.push(friendsAndFavorites);
      }

      return filters;
    }

    return [
      {
        label: `${translate('directory.directory.private.all.text')} ${translate(
          'directory.directory.private.shows.text'
        )}`,
        metaLabel: translate('directory.directory.private.all.text'),
        labelUrl: this._calculateLabelUrl(DEFAULT_FILTERS_VALUE),
        value: DEFAULT_FILTERS_VALUE,
        query: null,
      },
    ];
  }

  @computed get tokens() {
    const tokenOptions = getTokenOptions.map((option) => {
      const { range } = option;

      return {
        label: range.length === 1 ? `${range[0]}+` : `${range[0]}-${range[1]}`,
        value: range.reduce((acc, curr, idx) => `${acc + curr}${idx}`, 0),
        query: range.length === 1 ? { minPsPrice: range[0] } : { minPsPrice: range[0], maxPsPrice: range[1] },
      };
    });

    return [
      {
        label: translate('directory.directory.private.filters.all.shows'),
        value: DEFAULT_FILTERS_VALUE,
        query: null,
      },
      ...tokenOptions,
    ];
  }

  /**
   * @method kinks - returns aray of object of processed kinks
   */
  @computed get kinks() {
    if (this.kinksData) {
      const kinks = this.kinksData.map((kink) => {
        return {
          label: kink.name,
          value: kink.name,
          metaLabel: kink.name,
          labelUrl: this._calculateLabelUrl(kink.name, true),
          query: { showTag: kink.name },
        };
      });

      if (this.clickedKink) {
        if (
          !kinks.some((kink) => kink.value === this.clickedKink.value) &&
          this.clickedKink.value !== DEFAULT_FILTERS_VALUE
        ) {
          kinks.splice(0, 0, this.clickedKink);
        }
      }

      return [
        {
          label: translate('directory.directory.private.filters.all.shows'),
          metaLabel: translate('directory.directory.private.all.text'),
          labelUrl: this._calculateLabelUrl(DEFAULT_FILTERS_VALUE, true),
          value: DEFAULT_FILTERS_VALUE,
          query: null,
        },
        ...kinks,
      ];
    }

    return [
      {
        label: `${translate('directory.directory.private.all.text')} ${translate(
          'directory.directory.private.shows.text'
        )}`,
        metaLabel: translate('directory.directory.private.all.text'),
        labelUrl: this._calculateLabelUrl(DEFAULT_FILTERS_VALUE, true),
        value: DEFAULT_FILTERS_VALUE,
        query: null,
      },
    ];

  }

  /**
   * @method _handleFiltersResponse - handles the response of filters API
   * @param {Object} data
   */
  _handleFiltersResponse = (data) => {
    this.filtersData = data;
  };

  /**
   * @method _handleKinksResponse - handles the response of kinks API
   * @param {Object} data
   */
  _handleKinksResponse = (data) => {
    this.kinksData = data;
  };

  /**
   * @method _handleUserSettingsResponse - handles the response of user settings API
   * @param {Object} data
   */
  _handleUserSettingsResponse = (data) => {
    this.userPrivateShowSettings = data;
    if (data) {
      this.priceForBlockOfTimeAndPerMinute = {
        payByMinutePSInitialCharge: data.payByMinutePSInitialCharge,
        blocksOfTimeActive: data.blocksOfTimeActive,
        blocksOfTime: data.blocksOfTime,
      };
    }
  };

  /**
   * @method _handlePostCall - handles the call for PS to the broadcaster
   * @param {Object} data
   */
  _handlePostCall = (data) => {
    this.postResponse = data;
  };

  /**
   * @method _handleCancelCall - handles when user cancels the call to the broadcaster
   */
  _handleCancelCall = () => {
    this.postResponse = null;
    NotificationService.closeSnackbar();
  };

  /**
   * @method _calculateLabelUrl - creates the new url
   * @param {String} value - value of selected filter
   * @param {boolean} type - if it's kink or not
   * @returns
   */
  _calculateLabelUrl = (value, isKink) => {
    if (isKink) {
      if (value === DEFAULT_FILTERS_VALUE && this.filterRadioValue === DEFAULT_FILTERS_VALUE) {
        return '/';
      }

      if (value === DEFAULT_FILTERS_VALUE) {
        return `${this._slugifyUrl(this.filterRadioValue)}`;
      }

      return `${this._slugifyUrl(this.filterRadioValue)}/${value}`;
    }

    if (value === DEFAULT_FILTERS_VALUE) {
      return '/';
    }

    if (this.kinkRadioValue === DEFAULT_FILTERS_VALUE) {
      return `/${this._slugifyUrl(value)}`;
    }

    return `/${this._slugifyUrl(value)}/${this.kinkRadioValue}`;
  };

  /**
   * @method _slugifyUrl
   * @param {String} string
   * @returns new string
   */
  _slugifyUrl = (string) => string.replace(' & ', '-and-').replace(' ', '-').toLowerCase();

  /**
   * General
   */

  /**
   * @method runLoggedOutFlow - when user loggs out, reset filters / kinks to default state
   */
  runLoggedOutFlow = () => {
    PrivateDirectoryService.getFilters();
    PrivateDirectoryService.getKinks({ genderPreference: this.genderPreference });
  };

  /**
   * @method setAlreadyHasRequest - if the user already made a call to one broadcaster,
   * set the flag in case he wants to call another broadcaster,
   * then we need to cancel the first call, and make a new call
   * @param {bool} data - true / false
   */
  @action setAlreadyHasRequest = (data) => {
    this.alreadyHasRequest = data;
  };

  /**
   * @method init - initializes the state of the app depending if user is logged in or not
   * @param {Object} initObj - {username, realCountry, genderPreference, filter, kink}
   * @param {bool} isLoggedIn
   */
  @action init = (initObj, isLoggedIn) => {
    const { genderPreference, username } = initObj;
    this.genderPreference = genderPreference;
    this.activePage = DEFAULT_ACTIVE_PAGE;
    this.initValues = initObj;
    this.clickedKink = null;
    this.activeMobileView = VIEW.LIST;
    this.filterRadioValue = DEFAULT_FILTERS_VALUE;
    this.tokensRadioValue = DEFAULT_FILTERS_VALUE;
    this.kinkRadioValue = DEFAULT_FILTERS_VALUE;
    this.sortOptionValue = SORT_OPTION.VIDEO_QUALITY;
    this.performerName = null;

    this.getSliderCams();
    this.getCams();

    if (isLoggedIn) {
      this.getUserInfo(username);
    } else {
      this.runLoggedOutFlow();
    }
  };

  /**
   * @method getUserInfo - gets the user info for the init function
   * @param {String} username
   */
  getUserInfo = (username) => {
    this.filterRadioValue = DEFAULT_FILTERS_VALUE;
    this.kinkRadioValue = DEFAULT_FILTERS_VALUE;
    this.filtersData = null;
    this.kinksData = null;
    this.activePage = DEFAULT_ACTIVE_PAGE;

    ProfileService.getProfileInfo(username)
      .then(this._handleProfileInfoResponse)
      .catch((err) => {
        this.getCams();
        parseCatch(err);
      });
  };

  /**
   * @method _handleProfileInfoResponse - handles the info response for the user, gets the kinks and filters
   * @param {Object} data
   */
  _handleProfileInfoResponse = (data) => {
    this.profileInfo = data;
    PrivateDirectoryService.getFilters();
    PrivateDirectoryService.getKinks({ genderPreference: this.genderPreference });
  };

  /**
   * @method _decideWhaTitleToRender - decides what title to render depending on selected values (kinks/filters)
   * @param {String} defaultTitle
   * @param {String} additionalText
   * @returns {String} title
   */
  _decideWhaTitleToRender = (defaultTitle, additionalText) => {
    const showOnlyKinksTitle =
      this.filterRadioValue === DEFAULT_FILTERS_VALUE && this.kinkRadioValue !== DEFAULT_FILTERS_VALUE;
    const showOnlyFilterTitle =
      this.filterRadioValue !== DEFAULT_FILTERS_VALUE && this.kinkRadioValue === DEFAULT_FILTERS_VALUE;
    const showBothFilterAndKinks =
      this.filterRadioValue !== DEFAULT_FILTERS_VALUE && this.kinkRadioValue !== DEFAULT_FILTERS_VALUE;
    const showAll = this.filterRadioValue === DEFAULT_FILTERS_VALUE && this.kinkRadioValue === DEFAULT_FILTERS_VALUE;
    if (typeof additionalText === 'undefined') {
      additionalText = '';
    }

    const metaKinkLabel = this._getActiveKinkMetaLabel();
    const metaFilterLabel = this._getActiveFilterMetaLabel();
    const isC2CFilter = this.filterRadioValue === 'c2c';
    const metaFilterTitle = isC2CFilter ? metaFilterLabel : capitalize(metaFilterLabel);

    if (showOnlyKinksTitle && metaKinkLabel && metaFilterLabel) {
      return translate(`predefinedtags.${metaKinkLabel.toLowerCase()}`, {}, capitalize(metaKinkLabel)) + additionalText;
    } else if (showOnlyFilterTitle) {
      return metaFilterTitle + additionalText;
    } else if (showBothFilterAndKinks) {
      return (
        `${translate(`predefinedtags.${metaKinkLabel.toLowerCase()}`, {}, capitalize(metaKinkLabel))
        } | ${
          metaFilterTitle
        }${additionalText}`
      );
    } else if (showAll) {
      return defaultTitle;
    }

    return defaultTitle;

  };

  /**
   * @method handleSortChange - handles the sort options
   * @param value - selected value for sorting
   */
  @action handleSortChange = ({ value }) => {
    this.activePage = DEFAULT_ACTIVE_PAGE;
    this.sortOptionValue = value;
  };

  /**
   * @method setMobileActiveView - if mobile device change the view depending on user selection
   */
  @action setMobileActiveView = () => {
    this.setShowLoadingOverlay(false);
    if (!this.isEmptyState) {
      this.activeMobileView = this.activeMobileView === VIEW.LIST ? VIEW.SINGLE : VIEW.LIST;
    }
  };

  /**
   * @method handleUserNotificationChanged - handles if the broadcaster changed the PS settings
   * so the user needs to be notified
   */
  @action handleUserNotificationChanged = () => {
    this.userNotificationChanged = true;
    this._handleUserSettingsResponse();
  };

  /**
   * @method handleUserBalance - handles user balance of tokens
   * @param {number} data
   */
  @action handleUserBalance = (data) => {
    this.userBalance = data;
  };

  /**
   * @method userNotification - gets the notification from firebase (userNotification node)
   * @param {Object} data
   */
  @action userNotification = (data) => {
    this.userNotificationObj = data;
    this.postResponse = null;
  };

  @computed get paginationForcePage() {
    return this.activePage - DEFAULT_ACTIVE_PAGE;
  }

  /**
   * @method showPagination - shows the pagination if there is enough cams
   */
  @computed get showPagination() {
    return this.cams.totalCount > CAMS_PER_PAGE;
  }

  /**
   * @method paginationCount - calculates the number of pages to show in pagination
   */
  @computed get paginationCount() {
    return Math.ceil(this.cams.totalCount / CAMS_PER_PAGE);
  }

  /**
   * @method isPrevPageDisabled - disables previous page if active page is 1
   */
  @computed get isPrevPageDisabled() {
    return this.activePage === 1;
  }

  /**
   * @method isNextPageDisabled - disables the next page btn if the selected page is the last page
   */
  @computed get isNextPageDisabled() {
    return this.activePage === this.paginationCount;
  }

  /**
   * @method camsTitle - creates the title for the cams for Thumbnails
   */
  @computed get camsTitle() {
    const defaultTitle = translate('directory.directory.private.filters.all.shows');

    return this._decideWhaTitleToRender(defaultTitle);
  }

  /**
   * @method headingTitle - creates the heading title for the page
   */
  @computed get headingTitle() {
    const defaultTitle = translate('directory.directory.private.title');
    const addon = ` ${translate('directory.tab.privateshow')}`;

    return this._decideWhaTitleToRender(defaultTitle, addon);
  }

  /**
   * @method metaTitle - creates meta title for SEO
   */
  @computed get metaTitle() {
    const additionalText = ` ${translate('directory.directory.private.cams.live')}`;
    const defaultTitle = translate('directory.directory.private.default.title');

    return this._decideWhaTitleToRender(defaultTitle, additionalText);
  }

  /**
   * @method metaDescription - creates meta description for SEO depending on filters and kinks
   */
  @computed get metaDescription() {
    const defaultText = translate('directory.directory.private.index.meta.description');
    const filterText = translate('directory.directory.private.meta.description', { 0: capitalize(this.camsTitle) });

    return this._decideWhaTitleToRender(defaultText) === defaultText ? defaultText : filterText;
  }

  /**
   * @method showBuyTokensModal - triggers the buy tokens modal in case user does not have enough tokens
   */
  showBuyTokensModal = () => {
    this.messagingService.emit(SHOW_BUY_TOKENS_MODAL_TRIGGER);
  };

  _getActiveFilter = () => this.filters.find((filter) => filter.value === this.filterRadioValue);

  _getActiveTokens = () => this.tokens.find((token) => token.value === this.tokensRadioValue);

  _getActiveKink = () => this.kinks.find((kink) => kink.value === this.kinkRadioValue);

  _getActiveFilterQuery = () => this._getActiveFilter() && this._getActiveFilter().query;

  _getActiveTokensQuery = () => this._getActiveTokens() && this._getActiveTokens().query;

  _getActiveKinkQuery = () => this._getActiveKink() && this._getActiveKink().query;

  _getActiveFilterMetaLabel = () => this._getActiveFilter() && this._getActiveFilter().metaLabel;

  _getActiveKinkMetaLabel = () => this._getActiveKink() && this._getActiveKink().metaLabel;
}

export default new PrivateDirectoryStore();
